// Contracts — kids FINANCE (единая модель 11.07). Office rates (участнику +
// котёл), единый баланс звена, «Оплатить» (advance), каскад заработка по
// referred_by_id. Shapes snapped from migrations/0003 + 0023 + 0024.
//
// Money model: ALL amounts are INTEGER MINOR UNITS (cents). Balance of a
// moderator = SUM(mod_ledger.amount) over their rows (см. KidsLedgerTypeSchema
// ниже — advance(+) / adjust(±) / mod_earning(−) / referral_earning(−)).
//
// Scope (mirrors the admin cabinet): manager = all offices; admin = own office
// + own moderators only; moderator = self (balance/me).
import { z } from 'zod';

// ── shared bits ──────────────────────────────────────────────────────────────
// Ledger movement types. UNIFIED model (ТЗ Tim 11.07): один баланс, одна «Оплатить».
//   advance(+)          — «Оплатить»: деньги ВЫДАНЫ звену (аванс / погашение минуса)
//   adjust(±)           — ручная корректировка
//   mod_earning(−)      — заработал за свою done-сессию (своя доля с офис-ставки)
//   referral_earning(−) — маржа вышестоящего по цепочке referred_by_id
// Заработок хранится ОТРИЦАТЕЛЬНЫМ (уменьшает выданный аванс / становится «к выплате»):
//   balance = SUM. balance<0 ⇒ отснял больше выданного ⇒ надо «Оплатить». balance≥0 ⇒
//   остался невыработанный аванс. Оплата участнику НЕ трекается (его дело).
// Legacy-типы (topup/session_debit/payout) оставлены в enum только для парсинга
// старых строк; НОВЫЙ код их НЕ пишет.
export const KidsLedgerTypeSchema = z.enum([
  'advance', 'adjust', 'mod_earning', 'referral_earning',
  'passdown', // (−) «Выдано вниз»: зеркало advance у прямого родителя (реф-система: компания платит верху, вниз раздают сами)
  'topup', 'session_debit', 'payout', 'referral_margin', // legacy (parse-only)
]);
export type KidsLedgerType = z.infer<typeof KidsLedgerTypeSchema>;

// Minor units (cents). Signed amounts allow negatives (adjust); the unsigned
// variant gates topup (must be > 0).
const Cents = z.number().int();
const PositiveCents = z.number().int().positive();

// ── GET /api/kids/finance/office-rates ───────────────────────────────────────
// manager: all rates; admin: only own office's rate. Optional ?office= sub-filter
// (manager only; admin's is forced to own office regardless).
export const KidsOfficeRateSchema = z.object({
  office: z.string(),
  participantAmount: z.number().int(), // minor units paid to the participant
  // Office earnings pot per done-session («котёл 80», ТЗ 11.07). Default $80 (8000).
  // Manager-only editable; the top of the earnings chain earns this.
  modAmount: z.number().int().optional(),
  currency: z.string(),
  // Office cluster. Atlanta rates are FIXED at $40 (ATLANTA_FIXED_RATE_CENTS) and
  // served read-only (the rate editor is a Remote-only feature); Remote rates are
  // editable. Optional so the upsert-response reuse + historical callers stay valid.
  cluster: z.enum(['atlanta', 'remote']).optional(),
  // Кластер офиса вне финконтура (ТЗ Tim 12.07: Atlanta в финансах не ведём).
  // FE: строка серая с бейджем «вне финконтура», офис-модалка не открывается,
  // котёл read-only (PUT modAmount → 403 finance_excluded).
  financeExcluded: z.boolean().optional(),
});
export type KidsOfficeRate = z.infer<typeof KidsOfficeRateSchema>;

export const KidsOfficeRatesResSchema = z.object({
  rates: z.array(KidsOfficeRateSchema),
});
export type KidsOfficeRatesRes = z.infer<typeof KidsOfficeRatesResSchema>;

// ── PUT /api/kids/finance/office-rate ────────────────────────────────────────
// upsert one office's participant payout rate. manager: any office; admin: own
// office only (else 403). currency defaults to USD when omitted.
// Set office participant rate (admin/manager, Remote) and/or the office earnings
// pot modAmount (MANAGER only, all offices). At least one of the two amounts.
export const KidsOfficeRateUpsertReqSchema = z
  .object({
    office: z.string().trim().min(1).max(60),
    participantAmount: PositiveCents.optional(),
    modAmount: PositiveCents.optional(), // «котёл» офиса, manager-only
    currency: z.string().trim().length(3).toUpperCase().optional(),
    // Курсор продолжения ретро-пересчёта котла (nextAfter прошлого усечённого
    // ответа): без него повторное сохранение не знает, где остановился прогон.
    after: z.number().int().nonnegative().optional(),
  })
  .refine((d) => d.participantAmount != null || d.modAmount != null, 'participantAmount or modAmount required');
export type KidsOfficeRateUpsertReq = z.infer<typeof KidsOfficeRateUpsertReqSchema>;

export const KidsOfficeRateUpsertResSchema = z.object({
  ok: z.literal(true),
  rate: KidsOfficeRateSchema,
  // pending-бэкфилл + ретро-пересчёт по смене котла, суммой (0 когда нечего).
  // Optional so historical/foreign callers stay contract-valid.
  backfilled: z.number().int().nonnegative().optional(),
  // true → ретро-пересчёт котла усечён капом; продолжить = повторить сохранение
  // с after = nextAfter.
  truncated: z.boolean().optional(),
  nextAfter: z.number().int().nonnegative().optional(),
});
export type KidsOfficeRateUpsertRes = z.infer<typeof KidsOfficeRateUpsertResSchema>;

// ── GET /api/kids/finance/balances?office= ───────────────────────────────────
// per-moderator balance. manager: all (or ?office=); admin: own office only.
export const KidsModeratorBalanceSchema = z.object({
  moderatorId: z.string(),
  name: z.string(),
  pid: z.string().nullable(),
  office: z.string().nullable(),
  balance: z.number().int(), // SUM(mod_ledger.amount), minor units
  // Единая модель (11.07), optional для старых фикстур: earned =
  // −SUM(mod_earning + referral_earning) (≥0), toPay = max(0, −balance).
  earned: z.number().int().optional(),
  toPay: z.number().int().optional(),
  // The staff member's own per-done-session rate (users.mod_session_rate),
  // null = not assigned.
  modSessionRate: z.number().int().nullable().optional(),
});
export type KidsModeratorBalance = z.infer<typeof KidsModeratorBalanceSchema>;

export const KidsBalancesQuerySchema = z.object({
  office: z.string().trim().max(60).optional(), // manager only; admin forced to own
});
export type KidsBalancesQuery = z.infer<typeof KidsBalancesQuerySchema>;

export const KidsBalancesResSchema = z.object({
  balances: z.array(KidsModeratorBalanceSchema),
});
export type KidsBalancesRes = z.infer<typeof KidsBalancesResSchema>;

// ── GET /api/kids/finance/office-summary?office= ─────────────────────────────
// Финансовая сводка ОДНОГО офиса одним вызовом (экран «финансы офиса», ТЗ Tim
// 11.07): шапка-агрегаты + каждое staff-звено офиса со своими цифрами. manager:
// любой офис; admin: только свой (403 forbidden_office).
export const KidsOfficeSummaryQuerySchema = z.object({
  office: z.string().trim().min(1).max(60),
});
export type KidsOfficeSummaryQuery = z.infer<typeof KidsOfficeSummaryQuerySchema>;

export const KidsOfficeSummaryModSchema = z.object({
  moderatorId: z.string(),
  name: z.string(),
  pid: z.string().nullable(),
  role: z.string(),
  balance: Cents, // SUM(ledger); < 0 ⇒ мы должны
  earned: Cents, // −SUM(mod_earning+referral_earning); ≥0 в норме (легаси-знаки могут дать <0)
  toPay: Cents, // max(0, −balance)
  acceptedSessions: z.number().int().nonnegative(), // settleable-сессии этого звена
});
export type KidsOfficeSummaryMod = z.infer<typeof KidsOfficeSummaryModSchema>;

export const KidsOfficeSummaryResSchema = z.object({
  office: z.string(),
  // Офис вне финконтура (ТЗ 12.07): сводка не считается, все суммы нули,
  // moderators пуст — FE показывает плашку «финансы кластера не ведутся».
  excluded: z.boolean().optional(),
  participantAmount: Cents, // ставка участнику (Atlanta — фикс $40)
  modAmount: Cents, // котёл офиса (?? дефолт $80)
  acceptedSessions: z.number().int().nonnegative(), // settleable-сессии офиса
  earnedTotal: Cents, // Σ earned по звеньям офиса
  advancesTotal: Cents, // Σ advance-выдач по звеньям офиса
  toPayTotal: Cents, // Σ per-mod max(0, −balance)
  // РАСЧЁТНАЯ оценка выплат участникам: acceptedSessions × participantAmount.
  // Факт выплат участникам системой НЕ трекается (единая модель 11.07).
  participantCalc: Cents,
  moderators: z.array(KidsOfficeSummaryModSchema),
});
export type KidsOfficeSummaryRes = z.infer<typeof KidsOfficeSummaryResSchema>;

// ── GET /api/kids/finance/balance/me ─────────────────────────────────────────
// the calling moderator's own balance (for their cabinet).
export const KidsMyBalanceResSchema = z.object({
  moderatorId: z.string(),
  office: z.string().nullable(),
  balance: z.number().int(),
});
export type KidsMyBalanceRes = z.infer<typeof KidsMyBalanceResSchema>;

// ── POST /api/kids/finance/adjust ────────────────────────────────────────────
// manual signed correction → mod_ledger type=adjust. note is REQUIRED (an
// adjust must always carry a reason). amount may be negative but never 0.
export const KidsAdjustReqSchema = z.object({
  moderatorId: z.string().min(1),
  amount: Cents.refine((n) => n !== 0, 'amount must be non-zero'),
  note: z.string().trim().min(1).max(500),
});
export type KidsAdjustReq = z.infer<typeof KidsAdjustReqSchema>;

// shared ack for pay/adjust: the ledger row id + the moderator's new balance.
export const KidsLedgerWriteResSchema = z.object({
  ok: z.literal(true),
  ledgerId: z.string(),
  balance: z.number().int(),
});
export type KidsLedgerWriteRes = z.infer<typeof KidsLedgerWriteResSchema>;

// ── GET /api/kids/finance/ledger?moderatorId= ────────────────────────────────
// drill-down: a moderator's ledger rows, newest first. Scoped.
export const KidsLedgerQuerySchema = z.object({
  moderatorId: z.string().trim().min(1),
});
export type KidsLedgerQuery = z.infer<typeof KidsLedgerQuerySchema>;

export const KidsLedgerEntrySchema = z.object({
  id: z.string(),
  amount: z.number().int(),
  type: KidsLedgerTypeSchema,
  office: z.string().nullable(),
  refSessionId: z.number().int().nullable(),
  note: z.string().nullable(),
  createdAt: z.number().int(),
});
export type KidsLedgerEntry = z.infer<typeof KidsLedgerEntrySchema>;

export const KidsLedgerResSchema = z.object({
  moderatorId: z.string(),
  balance: z.number().int(),
  entries: z.array(KidsLedgerEntrySchema),
  // Единая модель (11.07) — семантика как в balances-строках; optional.
  earned: z.number().int().optional(),
  toPay: z.number().int().optional(),
});
export type KidsLedgerRes = z.infer<typeof KidsLedgerResSchema>;

// (Удалены 11.07 вместе с эндпоинтами единой модели: KidsTopupReq*,
// KidsSessionPaid*, KidsFinanceSessions* — оплата участнику не трекается,
// topup/payout слиты в POST /api/kids/finance/pay.)

// ── Multi-level finance (0023, ТЗ 07.07 «цепочка суб-ставок») ─────────────────

// ── PUT /api/kids/finance/mod-rate ───────────────────────────────────────────
// Назначить ставку звену за done-сессию (mod_session_rate, центы). Кто может:
// manager (любой офис) / admin (свой офис) / приведший назначает СВОИМ
// приведённым. amount=null очищает ставку (звено перестаёт зарабатывать).
// Правило маржи: назначаемая ставка ≤ ставки родителя по цепочке (иначе
// минус-маржа) — проверяется на бэке: 'rate_exceeds_parent' (выше потолка) или
// 'rate_ceiling_unset' (у родителя нет ставки — сперва задать её).
export const KidsSetModRateReqSchema = z.object({
  userId: z.string().min(1),
  modSessionRate: PositiveCents.nullable(), // null → снять ставку
  // Курсор продолжения пересчёта поддерева (nextAfter усечённого ответа):
  // пересчитанные сессии остаются settleable — без курсора повтор жевал бы
  // ту же голову и никогда не доходил до хвоста.
  after: z.number().int().nonnegative().optional(),
});
export type KidsSetModRateReq = z.infer<typeof KidsSetModRateReqSchema>;

export const KidsSetModRateResSchema = z.object({
  ok: z.literal(true),
  userId: z.string(),
  modSessionRate: z.number().int().nullable(),
  // сколько settleable-сессий поддерева пересчитано после установки ставки.
  backfilled: z.number().int().nonnegative().optional(),
  // true → пересчитана часть (cap); продолжение — повторный запрос с
  // after = nextAfter (UI ведёт сам: «нажмите ещё раз»).
  truncated: z.boolean().optional(),
  nextAfter: z.number().int().nonnegative().optional(),
});
export type KidsSetModRateRes = z.infer<typeof KidsSetModRateResSchema>;

// ── GET /api/kids/finance/invitees?moderatorId= ──────────────────────────────
// Приведённые этим звеном (по referred_by_id) + их ставки + свёрнутый заработок.
// Для реф-кабинета. Скоуп: manager/admin по офису; звено — только своих.
export const KidsInviteeSchema = z.object({
  userId: z.string(),
  name: z.string(),
  role: z.string(),
  pid: z.string().nullable(),
  office: z.string().nullable(),
  modSessionRate: z.number().int().nullable(), // ставка, назначенная этому звену
  doneSessions: z.number().int().nonnegative(), // сколько провёл (все done)
  // «начислено мне» — маржа вызывающего с сессий этого приведённого
  // (referral_earning по его сессиям), наружу положительным (прод-паритет).
  myMargin: z.number().int().nonnegative(),
});
export type KidsInvitee = z.infer<typeof KidsInviteeSchema>;

export const KidsInviteesResSchema = z.object({
  moderatorId: z.string(),
  ownRate: z.number().int().nullable(), // ставка самого звена (потолок для приведённых)
  invitees: z.array(KidsInviteeSchema),
});
export type KidsInviteesRes = z.infer<typeof KidsInviteesResSchema>;


// ── GET /api/kids/finance/payout-calc?from=&to= ──────────────────────────────
// «Расчёт выплат», ВАРИАНТ Б (Tim 11.07): отчёт по РЕАЛЬНЫМ начислениям единой
// модели за период (окно YYYY-MM-DD по created_at начислений в mod_ledger),
// а не «введённая сумма × сессии по статусу». amount = −SUM(mod_earning +
// referral_earning) получателя за окно (≥0, cents); sessions = его СВОИ
// отснятые сессии, начисленные в окне (строки mod_earning). Группировка
// блоками: офис получателя + имена админов офиса. Скоуп: manager → все офисы;
// admin → свой. XLSX собирается на фронте.
const PAYOUT_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const KidsPayoutCalcQuerySchema = z.object({
  from: z.string().regex(PAYOUT_DATE_RE).optional(),
  to: z.string().regex(PAYOUT_DATE_RE).optional(),
});
export type KidsPayoutCalcQuery = z.infer<typeof KidsPayoutCalcQuerySchema>;

export const KidsPayoutCalcRowSchema = z.object({
  moderatorId: z.string(),
  pid: z.string().nullable(),
  name: z.string(),
  sessions: z.number().int(), // своих сессий начислено в окне
  amount: z.number().int(),   // начислено всего за окно (cents, ≥0)
});
export type KidsPayoutCalcRow = z.infer<typeof KidsPayoutCalcRowSchema>;

export const KidsPayoutCalcGroupSchema = z.object({
  office: z.string().nullable(),    // null → получатели без офиса
  adminName: z.string().nullable(), // админ(ы) офиса, ', '-joined
  sessions: z.number().int(),
  amount: z.number().int(),
  rows: z.array(KidsPayoutCalcRowSchema),
});
export type KidsPayoutCalcGroup = z.infer<typeof KidsPayoutCalcGroupSchema>;

export const KidsPayoutCalcResSchema = z.object({
  groups: z.array(KidsPayoutCalcGroupSchema),
  totalSessions: z.number().int(),
  totalAmount: z.number().int(),
});
export type KidsPayoutCalcRes = z.infer<typeof KidsPayoutCalcResSchema>;

// ── GET /api/kids/finance/earnings?moderatorId= ──────────────────────────────
// Единая модель (ТЗ 11.07): «Заработал X» (моя доля с цепочки), баланс (выдано −
// заработано; <0 = к выплате), «Должен своим Y» (сумма к выплате моим прямым
// приведённым). Скоуп: manager/admin по офису; звено — себя.
export const KidsEarningsResSchema = z.object({
  moderatorId: z.string(),
  earned: z.number().int(), // «Заработал X» = −SUM(mod_earning+referral_earning) ≥ 0
  balance: z.number().int(), // SUM всего леджера; <0 = мы должны звену
  toPay: z.number().int(), // max(0, −balance) — сколько выплатить звену сейчас
  owedDown: z.number().int(), // «Должен своим Y» — Σ toPay прямых приведённых
  // «К выплате через тебя»: Σ toPay ВСЕГО поддерева referred_by_id (staff, без
  // самого звена). Осмысленно у верха цепочки — компания платит ему, вниз
  // раздаёт он (реф-система, ТЗ 12.07).
  subtreeToPay: z.number().int(),
  // true, когда админ скрыл баланс звену (show_balance=0) и оно читает себя.
  hidden: z.boolean().optional(),
});
export type KidsEarningsRes = z.infer<typeof KidsEarningsResSchema>;

// ── POST /api/kids/finance/pay ───────────────────────────────────────────────
// «Оплатить»: выдать деньги звену (аванс вперёд ИЛИ погашение минусового баланса)
// → mod_ledger type='advance' (+amount). Единственное денежное действие «сверху».
// manager/admin по офису, или приведший — своему приведённому (сверху вниз).
export const KidsPayReqSchema = z.object({
  moderatorId: z.string().min(1),
  amount: PositiveCents, // сколько выдано (центы, > 0)
  note: z.string().trim().max(500).optional(),
});
export type KidsPayReq = z.infer<typeof KidsPayReqSchema>;

// Ответ pay = write-res + passdown-исход. parentDebited=true ⇒ у прямого
// staff-родителя получателя атомарно списано то же ('passdown', −): он раздал из
// своего аванса; parentBalance — его баланс после. У верха цепочки (staff-родителя
// нет) parentDebited=false — платит компания напрямую.
export const KidsPayResSchema = KidsLedgerWriteResSchema.extend({
  parentDebited: z.boolean(),
  parentBalance: z.number().int().optional(),
});
export type KidsPayRes = z.infer<typeof KidsPayResSchema>;

// ── POST /api/kids/finance/reconcile-sweep?after= ────────────────────────────
// Manager-only обслуживание: пройти сессии, у которых состояние денег расходится
// с инвариантом «рассчитана» (settleable), и реконсилить их. Нужно при ДЕПЛОЕ
// перехода 10.07: старые «оплачено на done» деньги (posted, но не settleable)
// снимутся аккуратно разом, а не вразнобой на будущих синках. Пагинация курсором
// ?after=<id>; гонять, пока nextAfter != null.
export const KidsReconcileSweepQuerySchema = z.object({
  after: z.coerce.number().int().nonnegative().optional(),
});
export type KidsReconcileSweepQuery = z.infer<typeof KidsReconcileSweepQuerySchema>;

export const KidsReconcileSweepResSchema = z.object({
  ok: z.literal(true),
  processed: z.number().int().nonnegative(),
  nextAfter: z.number().int().nullable(), // null → больше нечего, проход завершён
});
export type KidsReconcileSweepRes = z.infer<typeof KidsReconcileSweepResSchema>;

// ── GET /api/kids/finance/earnings-detail?moderatorId=&from=&to=&after= ──────
// Прозрачность реф-системы (ТЗ Tim 12.07): «за что выплаты» — список done-сессий
// звена (свои + сессии сабмодов, давшие ему реферальную маржу) с вердиктом
// клиента и суммой начисления по каждой. Видно и «за что НЕ начислено»
// (pending/rejected → myAccrual null). Права: сам, его ПРЯМОЙ staff-реферер
// (он назначает ставку — ему видно, за что капает), manager/admin по офису.
// Окно from/to — по дате сессии (started_at); пагинация курсором after
// (последний session id страницы), страница 200.
export const KidsEarningsDetailQuerySchema = z.object({
  moderatorId: z.string().min(1),
  from: z.string().regex(PAYOUT_DATE_RE).optional(),
  to: z.string().regex(PAYOUT_DATE_RE).optional(),
  after: z.coerce.number().int().positive().optional(),
});
export type KidsEarningsDetailQuery = z.infer<typeof KidsEarningsDetailQuerySchema>;

export const KidsEarningsDetailVerdictSchema = z.enum(['accepted', 'invoiced', 'rejected', 'pending']);
export type KidsEarningsDetailVerdict = z.infer<typeof KidsEarningsDetailVerdictSchema>;

export const KidsEarningsDetailSessionSchema = z.object({
  sessionId: z.number().int(),
  date: z.number().int().nullable(), // started_at, epoch ms
  participant: z.string().nullable(), // имя ребёнка
  pid: z.string().nullable(), // P<modPid(2)><seq(4)> — как в мониторе/пакере
  verdict: KidsEarningsDetailVerdictSchema,
  myAccrual: z.number().int().nullable(), // начислено ЭТОМУ звену (cents, ≥0); null — не начислено
  kind: z.enum(['own', 'referral']), // своя сессия или сессия сабмода (маржа)
  subName: z.string().nullable(), // исполнитель, когда kind=referral
  accruedAt: z.number().int().nullable(), // когда начислено (epoch ms)
});
export type KidsEarningsDetailSession = z.infer<typeof KidsEarningsDetailSessionSchema>;

export const KidsEarningsDetailTotalsSchema = z.object({
  accepted: z.number().int().nonnegative(),
  invoiced: z.number().int().nonnegative(), // в счёте, ещё не принято/отклонено
  rejected: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(), // done без вердикта и без счёта
  accrued: z.number().int(), // начислено звену всего за окно (cents, ≥0)
});
export type KidsEarningsDetailTotals = z.infer<typeof KidsEarningsDetailTotalsSchema>;

export const KidsEarningsDetailResSchema = z.object({
  moderatorId: z.string(),
  sessions: z.array(KidsEarningsDetailSessionSchema),
  totals: KidsEarningsDetailTotalsSchema,
  nextAfter: z.number().int().nullable(), // курсор следующей страницы (session id)
  hidden: z.boolean().optional(), // show_balance=0 и звено читает себя
});
export type KidsEarningsDetailRes = z.infer<typeof KidsEarningsDetailResSchema>;
